/**
 * rother3 companion — theme (light / dark / follow the system)
 *
 * ## Why a toggle at all, when `prefers-color-scheme` already exists
 *
 * Because a media query is not a choice. A light theme with no opt-out traps the reader who is
 * sensitive to it, and a dark theme with no opt-out is forced on anyone whose OS is set to
 * light. Three states — light, dark, and *follow the system* — rather than a single toggle,
 * because a two-state toggle cannot express "I have not an opinion" and answering it by
 * defaulting to one is exactly the assumption being complained about.
 *
 * ## Why this runs as an inline script in the document head
 *
 * A module here would set `data-theme` **after** the browser has already painted, which shows
 * a flash of the wrong theme on every page load — worse than offering no choice at all,
 * because it makes the choice look broken. The stored value is read synchronously from
 * `localStorage` and the attribute is written before the first paint, so there is nothing to
 * flash. The module below owns the *control*; this owns the *first frame*.
 */
import { prefs, getPref } from './storage.js';

export const THEME_KEY = 'theme';

/** The three states. `'system'` removes the attribute, deferring to the media query. */
export const THEMES = /** @type {const} */ (['system', 'light', 'dark']);

/**
 * Read the stored preference.
 *
 * `getPref` rather than `localStorage` directly, so a private-mode browser that refuses
 * storage returns the default instead of throwing during page load. An exception here would
 * leave the page unstyled.
 */
export function currentTheme() {
  const value = getPref(THEME_KEY, 'system');
  return THEMES.includes(value) ? value : 'system';
}

/**
 * Apply a theme by writing `data-theme` on `<html>`.
 *
 * `'system'` **removes** the attribute rather than setting it to `"system"`. Both would work
 * today, because the CSS has no `[data-theme='system']` rule — but an attribute that matches
 * no rule is a trap for the next person who adds one, and removing it is the behaviour the
 * name implies.
 */
export function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'system') {
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', theme);
  }
}

/** Apply and remember. */
export function setTheme(theme) {
  const next = THEMES.includes(theme) ? theme : 'system';
  applyTheme(next);
  prefs.set(THEME_KEY, next);
  return next;
}

/**
 * The next state in the cycle.
 *
 * Names the **destination** rather than the state, because this feeds the accessible name and
 * the tooltip, where "is that what it is, or what it does?" has to be answerable without the
 * visual context a sighted reader has.
 */
export function nextTheme(theme) {
  return THEMES[(THEMES.indexOf(theme) + 1) % THEMES.length];
}

/**
 * The visible label for the toggle: the **current** state.
 *
 * Names where the page is now, not where it would go — a label is orientation, and the
 * accessible name beside it carries the action. (An earlier version of this comment claimed
 * the opposite and described the action, which is what `nextTheme` returns. The two are
 * different functions and the comment had them swapped.)
 *
 * The visible label stays true whichever way round the page is: "Theme: dark" on a dark page
 * is not ambiguous when the button's `aria-label` says "Switch to light".
 */
export function themeLabel(theme) {
  if (theme === 'system') {return 'Theme: follow system';}
  if (theme === 'light') {return 'Theme: light';}
  return 'Theme: dark';
}

/** The glyph, so the control is recognisable before its label is read. */
export function themeGlyph(theme) {
  if (theme === 'system') {return '◐';}
  if (theme === 'light') {return '☀';}
  return '☾';
}

/**
 * Mount the toggle into `#theme-toggle`.
 *
 * ## Why a `<span>` and not a `<button>` in the markup
 *
 * `initI18n()` overwrites the text content of every element carrying `data-i18n`, so a
 * `<button data-i18n>` would have its action label replaced by a static translation the moment
 * the locale loads — and the control would claim a state it is not in. Building the element in
 * JS keeps the label in one place and under the control of the module that knows the state.
 *
 * ## Why the accessible name is the action, not the state
 *
 * A screen reader announcing "Theme: light" on a button is genuinely ambiguous: it reads as a
 * label for a current value. `aria-label` therefore carries the *destination* — "Switch to dark"
 * — while the visible text names the current one, and `title` repeats the destination so a
 * mouse user sees it too. The two disagree on purpose: the visible label is for orientation,
 * the accessible name is for action.
 */
export function mountThemeToggle(container = document.getElementById('theme-toggle')) {
  if (container === null) {return null;}

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'theme-toggle-btn';

  const render = () => {
    const theme = currentTheme();
    button.textContent = `${themeGlyph(theme)} ${themeLabel(theme)}`;
    button.setAttribute('aria-label', `Switch to ${nextTheme(theme)}`);
    button.title = `Switch to ${nextTheme(theme)}`;
  };

  button.addEventListener('click', () => {
    setTheme(nextTheme(currentTheme()));
    render();
  });

  render();
  container.replaceChildren(button);
  return button;
}
